import type { SubscriptionPlan } from "@tdelivr/api-client/generated/tenant/tenant-entitlement.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the plans board does to the plans TEN-03 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `plans`:
 *
 * ```
 * plans:['Plans and entitlements','TEN-03','The plans tenants can buy, and each tenant's usage against them.',
 *   [['Plans','4','done',''],['Tenants near a limit','2','caution',''],
 *    ['Custom contracts','1','human','Enterprise'],['Renewals this quarter','3','waiting','']],
 *   ['Plan','Tenants','Limits'],
 *   [['Starter','1','1 brand · 50 drivers','done','live'],
 *    ['Growth','3','5 brands · 500 drivers','done','live'],
 *    ['Scale','0','20 brands · 5,000 drivers','done','live'],
 *    ['Enterprise','1','custom','human','contract']],
 *   'info','Changing a plan\\'s limits applies at each tenant\\'s next renewal.']
 * ```
 *
 * ## The recorded blocker made three claims and two of them still hold
 *
 * It read: _"Nothing on the platform models a plan: no name, no limits, no price. TEN-03 holds
 * entitlements one capability at a time, which is a different thing. The `Tenants · 3` column needs
 * GET /v1/tenants, which does not exist, and the entitlement write needs both a tenantId and a
 * capability this page would discover."_
 *
 * **The middle claim is closed as written and did not help.** ADR 0083 published `GET /v1/tenants`
 * — and that gives a count of tenants, not a count of tenants **per plan**, which is what this
 * column is. Nothing records which plan a tenant is on. That is a near miss worth naming, because
 * "the route it asked for now exists" is exactly the kind of fact that makes a stale blocker look
 * closed.
 *
 * **The first claim was true**, and [ADR 0092](../../../docs/adr/0092-a-plan-is-platform-wide-and-this-service-is-tenant-scoped.md)
 * records the plan. **The third is still true**, and the entitlement write stays off this board.
 *
 * ## Two of three columns are published and the third needs a field on another service
 *
 * | column    | source                                                                       |
 * | --------- | ----------------------------------------------------------------------------- |
 * | `Plan`    | `name` — a real name this time, not a code drawn where a name should be      |
 * | `Tenants` | **nothing.** No tenant records which plan it is on                           |
 * | `Limits`  | `limits`, joined the design's own way; `custom` where a contract plan has none |
 */

/** The most rows one request answers, which is this listing's own maximum. */
export const PER_FETCH = 200;

/**
 * The scope this board spends, re-exported from `navigation.ts` where every scope on this console
 * is declared — so the realm grant has one source and `channel-scopes.mjs` reads one literal.
 */
export { PLANS_READ_SCOPE } from "./navigation.ts";

/**
 * The design's `Limits` cell: `5 brands · 500 drivers`, or `custom`.
 *
 * **Singular and plural are the design's own**, and getting them from the number rather than from a
 * table is what keeps this honest: the design writes `1 brand · 50 drivers` and `20 brands · 5,000
 * drivers`, so the rule is "pluralise unless the limit is one". A capability whose name is already
 * plural — `api-calls` — is left as the service published it, because this console does not own
 * the vocabulary and un-pluralising somebody else's capability name is the kind of cleverness that
 * turns `brands` into `brand` on a row where it should not.
 *
 * `custom` where there are no limits, which is the design's own word for the `Enterprise` row.
 * That is **not** an absence: a contract plan genuinely has no published limits, and drawing the
 * dash that means "nothing publishes this" would say something false about a plan whose terms are
 * agreed per tenant.
 *
 * `null` is unlimited, and it says so rather than being dropped — a plan with an unlimited cap and
 * a plan with no cap recorded are different plans.
 */
export function limitsCell(plan: SubscriptionPlan): string {
  if (plan.limits.length === 0) return "custom";
  return plan.limits
    .map((entry) => {
      if (entry.limit === null) return `unlimited ${entry.capability}`;
      const name =
        entry.limit === 1 && entry.capability.endsWith("s")
          ? entry.capability.slice(0, -1)
          : entry.capability;
      return `${entry.limit.toLocaleString("en-US")} ${name}`;
    })
    .join(" · ");
}

/**
 * The design's two badge words, both published.
 *
 * `done · live` and `human · contract`. The design's `human` class is the one it reserves for a row
 * a person has to decide about, and a contract plan is exactly that — its price and its limits are
 * agreed per tenant rather than published, so somebody has to be asked.
 *
 * `retired` has no designed row and takes the service's own word under the design's `closed` class
 * — ADR 0088's decision for NTF-07's `draft`, and for the same reason.
 */
export function stateOf(plan: SubscriptionPlan): {
  classKey: StateClassKey;
  label: string;
  title: string;
} {
  if (plan.status === "retired")
    return {
      classKey: "closed",
      label: "retired",
      title:
        "TEN-03 publishes retired — withdrawn from sale. The design draws no row for this state, so the word is the service's. A tenant already on this plan is still on it, which is why a plan is retired rather than deleted.",
    };
  return plan.kind === "contract"
    ? {
        classKey: "human",
        label: "contract",
        title:
          "TEN-03 publishes kind=contract. Its price and its limits are agreed per tenant and are not published here, which is why the Limits cell reads custom rather than being empty.",
      }
    : {
        classKey: "done",
        label: "live",
        title: "TEN-03 publishes status=live and kind=standard — on sale, at a published price.",
      };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(plans: readonly SubscriptionPlan[]): readonly string[] {
  return [...new Set(plans.map((plan) => stateOf(plan).label))];
}

/** Whether a plan belongs under the chosen state filter. `""` is unfiltered. */
export function matchesState(plan: SubscriptionPlan, status: string): boolean {
  return status === "" || stateOf(plan).label === status;
}

/** Why the design's middle column is empty. */
export const TENANTS_ABSENT =
  "Nothing records which plan a tenant is on. ADR 0083 published GET /v1/tenants, which answers how many tenants exist and not how many are on each plan — so this column needs a plan reference on TEN-01's tenant, which is a field on another service's aggregate rather than a column here.";

/**
 * The price, with its currency and in full minor units, or the reason there is none.
 *
 * Not in the grid — the design's three columns are `Plan`, `Tenants` and `Limits`, and a price
 * appears only on its form. So this is a panel line, and it carries the money rule exactly as every
 * other amount on this platform does: a code and full minor units, never an abbreviation.
 */
export function priceLine(plan: SubscriptionPlan): string {
  if (plan.priceMinor === undefined || plan.priceCurrency === undefined) return "per contract";
  const major = Math.trunc(plan.priceMinor / 100);
  const minor = Math.abs(plan.priceMinor % 100);
  return `${plan.priceCurrency} ${major.toLocaleString("en-US")}.${String(minor).padStart(2, "0")} per month`;
}

/** One row of the plans board. */
export function planRow(plan: SubscriptionPlan): BoardRow {
  return {
    id: plan.planId,
    cells: [
      {
        /*
         * A real name, which is worth noting because three boards on this platform draw an
         * identifier here instead — the merchant payouts board draws a uuid, the replies board
         * draws a key, and the finance reports board refused to turn one into a title. TEN-03
         * publishes both a `code` and a `name`, and the design draws the name.
         */
        text: plan.name,
        title: `TEN-03 publishes the code ${plan.code}, which is how a contract refers to this plan and is what this board pages over.`,
      },
      {
        /*
         * The design's `1`, `3`, `0`, `1`. Nothing records which plan a tenant is on, so this is
         * the one column here with no source — and the zero in the design's third row is why a
         * guess would be worse than a dash: a board drawing `0` from nothing says a plan nobody is
         * on, which is a fact somebody would act on.
         */
        text: "",
        absent: true,
        title: TENANTS_ABSENT,
      },
      {
        text: limitsCell(plan),
        title:
          plan.limits.length === 0
            ? "A contract plan's limits are agreed per tenant and are not published. `custom` is the design's own word for this row, and is not the dash that means nothing publishes it."
            : `TEN-03 publishes ${String(plan.limits.length)} capability ${plan.limits.length === 1 ? "cap" : "caps"} on this plan, in the same vocabulary a tenant's own entitlements are written in.`,
      },
    ],
    state: stateOf(plan),
    detail: [
      { label: "Plan", value: plan.code, mono: true },
      { label: "Name", value: plan.name },
      { label: "Price", value: priceLine(plan) },
      { label: "Limits", value: limitsCell(plan) },
      { label: "Tenants on this plan", value: "—", absent: true },
    ],
    history: [
      { label: "Added", value: asDay(plan.createdAt) },
      { label: "Last changed", value: asDay(plan.updatedAt) },
      { label: "Version", value: `v${String(plan.version)}` },
    ],
    glyph: "workspace_premium",
  };
}

/** The panel's own absence, in words, because `DetailField` carries no title. */
export const DETAIL_NOTES: readonly { readonly label: string; readonly note: string }[] = [
  { label: "Tenants on this plan", note: TENANTS_ABSENT },
  {
    label: "Price",
    note: "A contract plan reads `per contract` rather than a dash: its price is agreed per tenant, which is a fact about how it is sold and not about what this platform can publish.",
  },
];

/** The design's own closing note, kept word for word. */
export const BOARD_NOTE = "Changing a plan's limits applies at each tenant's next renewal.";

/** The sentence under the grid, carrying what a reader is looking at and cannot see. */
export const LISTING_NOTE =
  "Every plan on the platform, in plan-code order. Tenants is empty because nothing records which plan a tenant is on — ADR 0083 published a listing of tenants, which answers how many exist and not how many are on each plan. The design's four tiles are not drawn: two are counts over this listing, Tenants near a limit needs each tenant's usage against its own entitlements, and Renewals this quarter needs a renewal date nothing holds.";

/**
 * Why the design's `New plan` form is drawn and not sent.
 *
 * Like the tenant administration console's `Add channel`, this is a **scope** rather than a missing
 * route: ADR 0092 publishes `POST /v1/platform/plans` taking a name, a kind, a price and a list of
 * limits — the form's own five controls, with its three numbers as limits. This console asks for
 * the read only, and granting the write is its own decision.
 */
export const NEW_PLAN_REFUSAL =
  "This board holds no write scope. TEN-03 does publish the command this form would send — its three numbers arrive as capability limits — so unlike most refusals on this console, this one is a permission rather than a missing route. Granting plan:platform:write is a decision with its own record.";
